/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { trackKeyUsage, isValidTranslationKey } from "helpers/trackUsedKeys";

/**
 * Translation lookup hook.
 *
 * Missing-key Critical alerts are owned solely by compareAndAlert (SSR post-render /
 * CSR post-load). This hook only records usage via trackKeyUsage — it must not call
 * Sentry, or load-gap / warm-cache races dual-fire false Criticals.
 */
const useTranslationHook = () => {
  // Two scalar selectors, not the whole appData slice: this hook runs in ~700 components,
  // and a slice-wide subscription re-rendered every one of them on any appData dispatch.
  const tolgeeTranslationKeys: any = useSelector(
    ({ appData }: AppState) => appData.tolgeeTranslationKeys,
  );
  const isLoadingTranslations: any = useSelector(
    ({ appData }: AppState) => appData.isLoadingTranslations,
  );

  const t = useCallback(
    (key: string, _value?: Record<string, string>, options?: { dynamic?: boolean }) => {
      try {
        const isDynamic = options?.dynamic ?? false;

        trackKeyUsage(key, isDynamic);

        if (!key || typeof key !== "string") {
          return key || "";
        }

        const langObj = tolgeeTranslationKeys;
        const resolvedValue = langObj ? langObj[key] : undefined;

        const applyInterpolation = (translated: string) => {
          if (typeof translated === "string" && _value) {
            return Object.keys(_value).reduce(
              (str, k) => str.replace(new RegExp(`{${k}}`, "g"), _value[k]),
              translated,
            );
          }
          return translated;
        };

        // While new route tags load, keep already-fetched keys visible (SPA nav).
        if (isLoadingTranslations) {
          if (resolvedValue !== undefined) {
            return applyInterpolation(resolvedValue);
          }
          if (!isDynamic && isValidTranslationKey(key, true)) {
            return "";
          }
          return key;
        }

        // Never render unresolved namespaced keys (e.g. "searchpage.foo.bar") in the UI.
        // Legacy English-as-key strings still fall back to themselves.
        if (resolvedValue === undefined) {
          if (!isDynamic && isValidTranslationKey(key, true)) {
            return "";
          }
          return key;
        }

        return applyInterpolation(resolvedValue);
      } catch (error) {
        if (key && isValidTranslationKey(key, true)) {
          return "";
        }
        return key || "";
      }
    },
    [tolgeeTranslationKeys, isLoadingTranslations],
  );

  return { t, isLoadingTranslations };
};

export default useTranslationHook;
